import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import {
  MDSDialog,
  MDSDialogSlotName,
  MDSButton,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSMenuDropdownSlotName,
  MDSButtonSlotName,
  MDSCheckbox,
  MDSButtonLoadingIndicator,
} from "@/mds";
import { Virtuoso } from "react-virtuoso";
import { NoteListItem } from "./note-list-item";
import { SortByKind } from "@/modules/lenses/types";
import { useEffect, useRef, useMemo } from "react";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import type { OneClickImportEntryPoint } from "@/store/one-click-import/types";
import type { ImportableNote } from "@/store/one-click-import/types";
import { ImportStatus, SectionSelectionState } from "@/store/one-click-import/types";
import { type ImportedNoteSortBy } from "@/store/one-click-import/AppStoreOneClickImportStore";
import type { FC } from "react";
import { lensModule } from "@/modules/lenses";
import { SortByDropdownButton } from "../filters/sort-by-dropdown-button";
import { MDSToggle } from "@/mds/components/forms/toggle";
import { CollectionSelectorDropdown } from "./collection-selector-dropdown";
import { pluralize } from "@/modules/pluralize";
import { ImportEmptyState } from "./import-empty-state";
import { ImportLoadingSkeleton } from "./import-loading-skeleton";
import { ImportModalHeader } from "./import-modal-header";
import { ImportFooterError } from "./import-footer-error";

type ListItem =
  | { type: "section"; section: string; displayLabel: string }
  | { type: "note"; note: ImportableNote };

const SORT_OPTIONS: ImportedNoteSortBy[] = [
  SortByKind.LastModified,
  SortByKind.LastCreated,
  SortByKind.Alphabetical,
];

export interface OneClickImportContentProps {
  onImportSuccess: () => void;
  onImportFailure?: () => void;
  onCancel: () => void;
  entryPoint: OneClickImportEntryPoint;
  /** Whether to show the cancel button in the footer */
  showCancelButton?: boolean;
  /** Whether the dialog close button is shown. Default true when not loading. */
  isDismissable?: boolean;
}

export const OneClickImportContent: FC<OneClickImportContentProps> = observer(
  function OneClickImportContent({
    onImportSuccess,
    onImportFailure,
    onCancel,
    entryPoint,
    showCancelButton = true,
    isDismissable,
  }) {
    const { store } = useAppStore();
    const hasTrackedListView = useRef(false);
    const isLoading = store.oneClickImport.isLoading;
    const isImporting = store.oneClickImport.isImporting;
    const hasLoadingError = store.oneClickImport.hasLoadingError;
    const errorMessage = store.oneClickImport.errorMessage;
    const totalCount = store.oneClickImport.totalNotesCount;
    const selectableCount = store.oneClickImport.selectableNotesCount;
    const lockedCount = totalCount - selectableCount;
    const source = store.oneClickImport.source;

    const groupedNotes = store.oneClickImport.notesGroupedByFolder;
    const filteredNotes = store.oneClickImport.filteredNotes;

    // Flatten grouped notes into a list with section headers
    // If there are no folders at all, show notes without sections
    const flatList = useMemo(() => {
      const items: ListItem[] = [];

      // If there are no groups (no folders at all), show notes without section headers
      if (groupedNotes.length === 0) {
        filteredNotes.forEach(note => {
          items.push({ type: "note", note });
        });
        return items;
      }

      // Otherwise, show notes grouped by folder sections
      groupedNotes.forEach(group => {
        items.push({
          type: "section",
          section: group.section,
          displayLabel: group.section || "Unfiled",
        });
        group.notes.forEach(note => {
          items.push({ type: "note", note });
        });
      });

      return items;
    }, [groupedNotes, filteredNotes]);

    // Track list view when content mounts with notes
    useEffect(() => {
      if (totalCount > 0 && !hasTrackedListView.current) {
        hasTrackedListView.current = true;
        trackEvent(TrackedEvent.OneClickImportListView, {
          source,
          total_notes_count: totalCount,
          selectable_notes_count: selectableCount,
          locked_notes_count: lockedCount,
          entry_point: entryPoint,
        });
      }
    }, [totalCount, selectableCount, lockedCount, entryPoint, source]);

    const handleCancel = () => {
      if (!isImporting && !isLoading) {
        store.oneClickImport.clearErrorMessage();
        trackEvent(TrackedEvent.OneClickImportCancel, {
          source: store.oneClickImport.source,
          selected_count: store.oneClickImport.selectedNotesCount,
          total_count: totalCount,
          entry_point: entryPoint,
        });
        void store.oneClickImport.closeAppleNotesReader();
        onCancel();
      }
    };

    const handleRetryLoadNotes = () => {
      store.oneClickImport.clearErrorMessage();
      store.oneClickImport.retryLoadNotes();
    };

    const handleFilterChange = (value: string) => {
      store.oneClickImport.setFilterQuery(value);
    };

    const handleClearFilter = () => {
      store.oneClickImport.setFilterQuery("");
    };

    const handleSortChange = (sortBy: SortByKind) => {
      // Only accept valid sort options for imported notes
      if (SORT_OPTIONS.includes(sortBy as ImportedNoteSortBy)) {
        store.oneClickImport.setSortOption(sortBy as ImportedNoteSortBy);
      }
    };

    const handleImport = async () => {
      // Clear any previous error when attempting a new import
      store.oneClickImport.clearErrorMessage();

      const notesToImport = store.oneClickImport.selectedNotes;

      if (notesToImport.length === 0) {
        store.oneClickImport.setErrorMessage("Please select at least one note to import");
        return;
      }

      // Analytics and isImporting state are now handled in the store
      const result = await store.oneClickImport.importSelectedNotes();

      // In case of error modal should stay open to show error message / allow retry
      if (result.success) {
        store.oneClickImport.closeModal({ status: ImportStatus.Success });
        store.oneClickImport.clear();

        onImportSuccess?.();

        return;
      }

      onImportFailure?.();
    };

    const selectedCount = store.oneClickImport.selectedNotesCount;
    const hasNotes = totalCount > 0;

    // Default isDismissable to true when not loading/importing
    const dialogDismissable = isDismissable ?? (!isLoading && !isImporting);

    const renderItem = (_index: number, item: ListItem) => {
      if (item.type === "section") {
        // Use the actual section value for store calls, not the display label
        const selectionState = store.oneClickImport.getSectionSelectionState(item.section);

        const handleSectionToggle = () => {
          store.oneClickImport.toggleSectionSelection(item.section);
        };

        return (
          <StyledSectionHeader>
            <MDSCheckbox
              isSelected={selectionState === SectionSelectionState.All}
              isIndeterminate={selectionState === SectionSelectionState.Indeterminate}
              onChange={handleSectionToggle}
            >
              <MDSText
                color={MDSTextColor.Secondary}
                variant={MDSTextVariant.TextXs}
                weight={MDSTextWeight.SemiBold}
              >
                {item.displayLabel}
              </MDSText>
            </MDSCheckbox>
          </StyledSectionHeader>
        );
      }
      return <NoteListItem key={item.note.id} note={item.note} />;
    };

    return (
      <StyledWideDialog isOpen={true} handleClose={handleCancel} isDismissable={dialogDismissable}>
        <ImportModalHeader slot={MDSDialogSlotName.Header} />

        <StyledContent slot={MDSDialogSlotName.Content}>
          <StyledFilterContainer>
            <StyledFilterTextField
              variant={MDSTextFieldVariant.Default}
              placeholder="Filter by title"
              value={store.oneClickImport.filterQuery}
              onChange={handleFilterChange}
              onClearText={handleClearFilter}
              showClearTextButton
              aria-label="Filter notes by title"
            >
              <MDSIcon
                slot={MDSTextFieldSlotName.LeftAddon}
                kind={MDSIconKind.Search}
                color={MDSIconColor.Secondary}
              />
            </StyledFilterTextField>
            <SortByDropdownButton
              sortOptions={SORT_OPTIONS}
              defaultSortOption={SortByKind.LastModified}
              selectedSortOption={store.oneClickImport.sortOption}
              onSelectSortOption={handleSortChange}
              customTrigger={(isOpen: boolean) => (
                <MDSButton
                  slot={MDSMenuDropdownSlotName.Trigger}
                  variant={MDSButtonVariant.Transparent}
                >
                  {lensModule.sortKindLabelMap[store.oneClickImport.sortOption]}

                  {isOpen ? (
                    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleUp} />
                  ) : (
                    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleDown} />
                  )}
                </MDSButton>
              )}
            />
          </StyledFilterContainer>
          <StyledContentWrapper>
            {isLoading || isImporting ? (
              <ImportLoadingSkeleton />
            ) : hasNotes && flatList.length > 0 ? (
              <StyledVirtuosoWrapper>
                <Virtuoso
                  data={flatList}
                  itemContent={renderItem}
                  style={{
                    height: "400px",
                    scrollbarWidth: "none",
                    transition: "opacity 0.1s ease-in-out",
                  }}
                />
              </StyledVirtuosoWrapper>
            ) : (
              <ImportEmptyState totalNotesCount={totalCount} />
            )}
          </StyledContentWrapper>
        </StyledContent>

        <StyledFooter slot={MDSDialogSlotName.Footer}>
          {!isLoading &&
            !isImporting &&
            !hasLoadingError &&
            hasNotes &&
            (store.oneClickImport.tags.length > 0 || store.oneClickImport.folders.length > 0) && (
              <StyledCollectionOptionsContainer>
                {store.oneClickImport.tags.length > 0 && (
                  <StyledCollectionOptionRow>
                    <StyledToggleContainer>
                      <MDSToggle
                        checked={store.oneClickImport.useTagsAsCollections}
                        onChange={store.oneClickImport.toggleUseTagsAsCollections}
                      />
                      <MDSText variant={MDSTextVariant.TextS}>
                        Convert {store.oneClickImport.selectedTagNames.length}{" "}
                        {pluralize(store.oneClickImport.selectedTagNames.length, "tag", "tags")} to
                        collections
                      </MDSText>
                    </StyledToggleContainer>
                    <CollectionSelectorDropdown
                      label="Choose specific tags"
                      items={store.oneClickImport.filteredTags}
                      selectedItems={store.oneClickImport.selectedTagNames}
                      onSelectionChange={store.oneClickImport.setSelectedTagNames}
                      isDisabled={!store.oneClickImport.useTagsAsCollections}
                      getCollectionId={store.oneClickImport.getCollectionIdForTag}
                      getNotesCount={store.oneClickImport.getNotesCountForTag}
                      searchQuery={store.oneClickImport.tagSearchQuery}
                      onSearchQueryChange={store.oneClickImport.setTagSearchQuery}
                    />
                  </StyledCollectionOptionRow>
                )}

                {store.oneClickImport.folders.length > 0 && (
                  <StyledCollectionOptionRow>
                    <StyledToggleContainer>
                      <MDSToggle
                        checked={store.oneClickImport.useFoldersAsCollections}
                        onChange={store.oneClickImport.toggleUseFoldersAsCollections}
                      />
                      <MDSText variant={MDSTextVariant.TextS}>
                        Convert {store.oneClickImport.selectedFolderNames.length}{" "}
                        {pluralize(
                          store.oneClickImport.selectedFolderNames.length,
                          "folder",
                          "folders"
                        )}{" "}
                        to collections
                      </MDSText>
                    </StyledToggleContainer>
                    <CollectionSelectorDropdown
                      label="Choose specific folders"
                      items={store.oneClickImport.filteredFolders}
                      selectedItems={store.oneClickImport.selectedFolderNames}
                      onSelectionChange={store.oneClickImport.setSelectedFolderNames}
                      isDisabled={!store.oneClickImport.useFoldersAsCollections}
                      getCollectionId={store.oneClickImport.getCollectionIdForFolder}
                      getNotesCount={store.oneClickImport.getNotesCountForFolder}
                      searchQuery={store.oneClickImport.folderSearchQuery}
                      onSearchQueryChange={store.oneClickImport.setFolderSearchQuery}
                    />
                  </StyledCollectionOptionRow>
                )}
              </StyledCollectionOptionsContainer>
            )}
          <StyledFooterActions>
            <StyledFooterLeft>
              <ImportFooterError message={errorMessage} />
            </StyledFooterLeft>
            {showCancelButton && (
              <MDSButton
                variant={MDSButtonVariant.Transparent}
                onClick={handleCancel}
                isDisabled={isImporting || isLoading}
              >
                Cancel
              </MDSButton>
            )}
            {hasLoadingError ? (
              <MDSButton
                variant={MDSButtonVariant.Contrast}
                onClick={handleRetryLoadNotes}
                isLoading={isLoading}
              >
                Try again
              </MDSButton>
            ) : (
              <MDSButton
                variant={MDSButtonVariant.Contrast}
                onClick={handleImport}
                isDisabled={isLoading || !hasNotes || selectedCount === 0 || isImporting}
                isLoading={isImporting}
                loadingIndicator={MDSButtonLoadingIndicator.Icon}
              >
                {isImporting
                  ? "Importing..."
                  : `Import ${selectedCount} ${pluralize(selectedCount, "note", "notes")}`}
              </MDSButton>
            )}
          </StyledFooterActions>
        </StyledFooter>
      </StyledWideDialog>
    );
  }
);

const StyledWideDialog = styled(MDSDialog)`
  width: min(1280px, 90vw);
  max-width: 90vw;
`;

const StyledContent = styled.div`
  position: relative;
  width: 100%;
  padding-left: ${({ theme }) => theme.spacing[3]};
  padding-right: ${({ theme }) => theme.spacing[3]};
`;

const StyledContentWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledVirtuosoWrapper = styled.div`
  overflow: hidden;
`;

const StyledSectionHeader = styled.header`
  display: flex;
  align-items: start;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};
  padding-inline: ${({ theme }) => theme.spacing["0"]};
  user-select: none;
  text-transform: uppercase;
  letter-spacing: 0.24px;

  label {
    text-transform: uppercase;
    letter-spacing: 0.24px;
    cursor: pointer;
  }
`;

const StyledFilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["3"]};
  width: 100%;
  padding-bottom: ${({ theme }) => theme.spacing["0.5"]};

  > :first-child {
    width: 100%;
    max-width: 420px;
  }
`;

const StyledFilterTextField = styled(MDSTextField)`
  min-height: 32px;
  padding-top: ${({ theme }) => theme.spacing["1"]};
  padding-bottom: ${({ theme }) => theme.spacing["1"]};
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  width: 100%;
`;

const StyledFooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
  width: 100%;
`;

const StyledFooterLeft = styled.div`
  flex: 1;
`;

const StyledCollectionOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  width: 100%;
`;

const StyledCollectionOptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-left: ${({ theme }) => theme.spacing["2"]};
`;

const StyledToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
